/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit'
import { LiveReportServiceErrorResponse, ErrorCodes } from 'liveReportService'
import { getCsrfToken, login, logout, getReportConfig } from './asyncThunks'
import { Status } from '../types'

export interface LiveReportSlice {
  csrfTokenState: {
    status: Status
    token?: string
    error?: LiveReportServiceErrorResponse
  }
  universalError?: LiveReportServiceErrorResponse
  isValidSession?: boolean
  loginState: {
    status: Status
    error?: LiveReportServiceErrorResponse
  }
  isProtectedReport?: boolean
  reportId?: string
  buildName?: string
}

const initialState: LiveReportSlice = {
  csrfTokenState: {
    status: 'idle',
    token: undefined,
    error: undefined
  },
  universalError: undefined,
  loginState: {
    status: 'idle',
    error: undefined
  },
  isProtectedReport: undefined,
  reportId: undefined,
  buildName: undefined
}

const liveReportSlice = createSlice({
  name: 'liveReport',
  initialState,
  reducers: {
    setUniversalError: (state, action) => {
      state.universalError = action.payload
      if (
        state.isProtectedReport !== false &&
        action.payload?.status === ErrorCodes.InvalidSessionId
      ) {
        state.isValidSession = false
      }
    },
    setIsValidSession: (state, action) => {
      state.isValidSession = action.payload
    },
    setReportId: (state, action) => {
      state.reportId = action.payload
    },
    setBuildName: (state, action) => {
      state.buildName = action.payload
    },
    resetCsrfToken: (state) => {
      state.csrfTokenState.status = 'idle'
      state.csrfTokenState.token = undefined
      state.csrfTokenState.error = undefined
    },
    resetLoginState: (state) => {
      state.loginState.status = 'idle'
      state.loginState.error = undefined
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCsrfToken.pending, (state) => {
        state.csrfTokenState.status = 'loading'
        state.csrfTokenState.token = undefined
        state.csrfTokenState.error = undefined
      })
      .addCase(getCsrfToken.fulfilled, (state, action) => {
        state.csrfTokenState.status = 'succeeded'
        state.csrfTokenState.token = action.payload.csrfToken
        state.isValidSession = true
      })
      .addCase(getCsrfToken.rejected, (state, action) => {
        state.csrfTokenState.status = 'failed'
        state.csrfTokenState.error = action.payload
      })
      .addCase(login.pending, (state) => {
        state.loginState.status = 'loading'
        state.loginState.error = undefined
      })
      .addCase(login.fulfilled, (state) => {
        state.loginState.status = 'succeeded'
        state.isValidSession = true
        state.universalError = undefined
      })
      .addCase(login.rejected, (state, action) => {
        state.loginState.status = 'failed'
        state.loginState.error = action.payload
      })
      .addCase(logout.fulfilled, (state) => {
        state.isValidSession = false
      })
      .addCase(getReportConfig.fulfilled, (state, action) => {
        state.isProtectedReport = !action.payload.anonymous
      })
  }
})

export const {
  setUniversalError,
  setIsValidSession,
  setReportId,
  setBuildName,
  resetCsrfToken,
  resetLoginState
} = liveReportSlice.actions

export default liveReportSlice.reducer
